import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from 'src/app/core/models/user';
import { UserService } from '../open-api/user.service';
import { Account } from '@/core/models/account';

/**
 * @class LicenseState
 *
 * **Attention:** Please note, that the LicenseState service will only work properly for users with permissions,
 * that allow seeing all users! Otherwise, the active user calculation will not work correctly.
 *
 * For getting the current users (accounts) license state, please use userService.currentUser.isPro().
 *
 */
@Injectable({ providedIn: 'root' })
export class LicenseState {
    constructor(private userService: UserService) {}

    getLicense(): Observable<License> {
        return this.userService.loadAccount().pipe(
            map((account: Account) => {
                const trialStart = account.trialStart;
                let currentLicense = CurrentLicense.FREE;
                let trialDaysLeft = 0;
                let trialState = TrialState.UNUSED;

                const activeUserCount = account.getActiveUserCount();

                if (account.hasEnoughProLicenses()) {
                    currentLicense = CurrentLicense.PRO;
                }

                if (trialStart !== null) {
                    const trialStartDate = new Date(trialStart);
                    trialDaysLeft = LicenseState.calculateTrialDaysLeft(trialStartDate);
                    if (trialDaysLeft > 0 && currentLicense != CurrentLicense.PRO) {
                        trialState = TrialState.ACTIVE;
                        currentLicense = CurrentLicense.TRIAL;
                    }
                    if (trialDaysLeft <= 0 && trialStartDate != new Date('2023-01-01')) {
                        trialState = TrialState.EXPIRED;
                    }
                }

                const license: License = {
                    licenses: account.licenses,
                    users: account.getUsers(),
                    activeUsers: activeUserCount,
                    feature: {
                        currentLicense: currentLicense,
                        trial: {
                            trialState,
                            trialDaysLeft,
                        },
                    },
                };
                return license;
            }),
        );
    }

    public static calculateTrialDaysLeft(trialStart: Date): number {
        const today = new Date();
        const cutoffDate = new Date('2023-04-29');

        if (trialStart <= cutoffDate) {
            const diffInTime = Math.abs(today.getTime() - trialStart.getTime());
            const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
            return Math.max(0, Math.min(15, 15 - diffInDays));
        } else {
            const diffInTime = trialStart.getTime() - today.getTime();
            const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
            let trialDaysLeft = Math.max(0, Math.min(30, diffInDays));

            if (trialStart > today && trialDaysLeft === 0) {
                trialDaysLeft = Math.ceil(diffInTime / (1000 * 3600 * 24));
            } else if (trialStart < today) {
                trialDaysLeft = 0;
            }

            return trialDaysLeft;
        }
    }
}

export interface License {
    licenses: number;
    users: User[];
    activeUsers: number;
    feature: Feature;
}

export interface Feature {
    currentLicense: CurrentLicense;
    trial: Trial;
}

export interface Trial {
    trialState: TrialState;
    trialDaysLeft: number;
}

export enum CurrentLicense {
    FREE,
    TRIAL,
    PRO,
}
export function getCurrentLicenseFromString(license: string): CurrentLicense {
    switch (license) {
        case 'FREE':
            return CurrentLicense.FREE;
        case 'PRO':
            return CurrentLicense.PRO;
        case 'TRIAL':
            return CurrentLicense.TRIAL;
        default:
            console.error('Unexpected license type: ' + license);
            return CurrentLicense.FREE;
    }
}

export function getLicenseString(license: CurrentLicense | undefined): string {
    switch (license) {
        case CurrentLicense.FREE:
            return 'FREE';
        case CurrentLicense.PRO:
            return 'PRO';
        case CurrentLicense.TRIAL:
            return 'TRIAL';
        default:
            console.error('Unexpected license type: ' + license);
            return 'FREE';
    }
}

export enum TrialState {
    UNUSED,
    ACTIVE,
    EXPIRED,
}
